/**
 * Agent activity — what the agent is doing right now, read off the wire.
 *
 * The chat panel's loader used to guess: it matched the shopper's words and
 * the current URL against a list and rotated through lines on a timer, so
 * "show me red dresses" could read "Loading your cart…" on the bag page.
 * The backend already says what it is doing, frame by frame:
 *
 *   agent_thinking, llm_start             → thinking
 *   llm_response   {tools:["search_…"]}   → that tool, announced
 *   tool_call      {tool}                 → that tool, called
 *   every later frame of the turn         → still the most recent tool
 *
 *   and in a turn that names no tool:
 *   llm_response   (the answer)           → wrapping
 *   completion                            → wrapping
 *
 * A tool, once named, stays named until the turn ends. The tool itself runs
 * in milliseconds; what the shopper waits for is the model writing its reply
 * around the result, and nothing they asked for (the bag opening, the piece
 * landing in it) shows until the turn is over. Naming the tool only while it
 * ran gave the one informative line about 6 ms of a two-second turn.
 *
 * This module turns that sequence into one small state, and the state into
 * one line of copy. Framework-free and pure, so the wire contract can be
 * tested with frames copied verbatim from the network tab.
 */

export const AgentPhase = Object.freeze({
  /** Voice turn: audio sent, waiting for the transcript. */
  TRANSCRIBING: "transcribing",
  /** The model is deciding what to do; no tool has been named yet. */
  THINKING: "thinking",
  /**
   * The turn has reached for a tool — `activity.tool` is the most recent one
   * named. Holds through the tool's result and the reply written around it,
   * until the turn ends.
   */
  TOOL: "tool",
  /** A turn that named no tool: the answer has arrived and the turn is closing. */
  WRAPPING: "wrapping",
});

/** Compare tool ids across backends (`View_Cart`, `view-cart`, `view_cart`). */
export function normalizeAgentToolName(name) {
  if (typeof name !== "string") return "";
  const s = name.trim().toLowerCase();
  if (!s) return "";
  return s.replace(/-/g, "_");
}

/**
 * Tool names the model announced on an `llm_response` (`data.tools`). They
 * arrive with the preamble, a beat before the matching `tool_call` frames.
 */
function announcedTools(data) {
  const list = Array.isArray(data?.tools) ? data.tools : [];
  return list
    .map((entry) =>
      normalizeAgentToolName(
        typeof entry === "string"
          ? entry
          : entry?.tool ?? entry?.name ?? entry?.function?.name
      )
    )
    .filter(Boolean);
}

/**
 * Reduce one turn's frames to `{ phase, tool }` and report each change.
 *
 * One tracker per turn. It stays silent until `start()` — the turn is only
 * ours once the socket accepted the send — and after `completion`, because a
 * straggler (a `tool_call` that lost the race with `completion`) must not
 * rename a turn that is already over.
 *
 * @param {(activity: { phase: string, tool: string | null }) => void} onChange
 */
export function createAgentActivityTracker(onChange) {
  if (typeof onChange !== "function") {
    return { start() {}, handleFrame() {} };
  }

  let started = false;
  let completed = false;
  /** @type {{ phase: string, tool: string | null } | null} */
  let current = null;

  const publish = (phase, tool = null) => {
    if (current && current.phase === phase && current.tool === tool) return;
    current = { phase, tool };
    try {
      onChange(current);
    } catch (err) {
      console.warn("[WS] onActivity threw", err);
    }
  };

  /**
   * Thinking and wrapping describe a turn with no tool in it. Once a tool is
   * named the turn is about that tool until it ends, so these steps no
   * longer apply — and since nothing else leaves the tool phase, being in it
   * is all the memory "a tool was named" needs.
   */
  const publishUnlessToolNamed = (phase) => {
    if (current?.phase !== AgentPhase.TOOL) publish(phase);
  };

  return {
    /** @param {boolean} [isVoiceTurn] audio was sent; a transcript comes first */
    start(isVoiceTurn = false) {
      if (started) return;
      started = true;
      publish(isVoiceTurn ? AgentPhase.TRANSCRIBING : AgentPhase.THINKING);
    },

    /** Feed every parsed server frame of the turn, in arrival order. */
    handleFrame(frame) {
      if (!started || completed || !frame || typeof frame !== "object") return;
      const data =
        frame.data != null && typeof frame.data === "object" ? frame.data : {};

      switch (frame.type) {
        case "transcript":
          if (current?.phase === AgentPhase.TRANSCRIBING) {
            publish(AgentPhase.THINKING);
          }
          break;

        case "message":
          // The server echoes the recognised utterance as a user message.
          if (
            data.source === "user" &&
            current?.phase === AgentPhase.TRANSCRIBING
          ) {
            publish(AgentPhase.THINKING);
          }
          break;

        // The model has the floor. That is news only until a tool is named:
        // the model step after a tool is the reply being written around its
        // result, which the tool's own line already covers.
        case "agent_thinking":
        case "llm_start":
          publishUnlessToolNamed(AgentPhase.THINKING);
          break;

        case "llm_response": {
          // The calls follow in the order announced, so the first is next.
          const [announced] = announcedTools(data);
          if (announced) {
            publish(AgentPhase.TOOL, announced);
          } else if (data.source !== "tool_preamble") {
            // The answer itself.
            publishUnlessToolNamed(AgentPhase.WRAPPING);
          }
          break;
        }

        case "tool_call": {
          // The most recent call names the turn. A call the frame does not
          // name tells us nothing newer than the tool already showing.
          const tool = normalizeAgentToolName(data.tool ?? data.name);
          publish(AgentPhase.TOOL, tool || current?.tool || null);
          break;
        }

        case "completion":
          completed = true;
          publishUnlessToolNamed(AgentPhase.WRAPPING);
          break;

        // `tool_result` is deliberately not a step: the tool stays named
        // until the turn ends, so there is nothing to settle — and a result
        // that never comes (stateless tools such as clear_cart send none) or
        // comes out of order cannot disturb the label.
        default:
          break;
      }
    },
  };
}

/** A tool with no rule of its own: true of anything the agent might run. */
const GENERIC_TOOL_LINES = [
  "Working on it…",
  "Taking care of that…",
  "Seeing to that for you…",
];

/**
 * Loader copy for one tool. Matched by shape rather than by exact name, so a
 * tool the backend adds or respells (`get_new_arrivals`, `View-Cart`) still
 * gets a truthful line. Order matters: the specific rules sit above the
 * generic ones ("remove_from_wishlist" must not fall through to the bag).
 * Copy is in the stylist's voice and says "bag", like the rest of the
 * revamped storefront. To reword a tool, edit its list here and nowhere else.
 *
 * A tool's lines stay up from its announcement until the turn ends, so each
 * must hold for that whole stretch: say what the shopper is waiting for
 * ("Opening your bag…"), never that the tool has finished or what it found.
 */
function toolLines(tool) {
  const t = normalizeAgentToolName(tool);
  if (!t) return GENERIC_TOOL_LINES;

  if (/price_?match|match_?price/.test(t)) {
    return [
      "Comparing prices for you…",
      "Checking what others are asking…",
      "Seeing what we can do on the price…",
    ];
  }

  if (/try_?on/.test(t)) {
    return /close|stop|end|exit/.test(t)
      ? ["Closing the fitting room…", "Putting the mirror away…"]
      : [
          "Preparing the fitting room…",
          "Setting up your mirror…",
          "Getting your look ready…",
        ];
  }

  if (/wishlist|favou?rite/.test(t)) {
    if (/remove|delete/.test(t)) {
      return ["Taking it off your wishlist…", "Tidying your wishlist…"];
    }
    if (/add|save/.test(t)) {
      return [
        "Saving it to your wishlist…",
        "Setting it aside for you…",
        "Keeping this one for later…",
      ];
    }
    return [
      "Opening your wishlist…",
      "Gathering the pieces you saved…",
      "Bringing out your saved pieces…",
    ];
  }

  if (/cart|bag|basket/.test(t)) {
    if (/clear|empty/.test(t)) {
      return ["Emptying your bag…", "Clearing everything out…"];
    }
    if (/remove|delete/.test(t)) {
      return ["Taking it out of your bag…", "Lightening your bag…"];
    }
    if (/update|quantity|qty/.test(t)) {
      return [
        "Updating your bag…",
        "Adjusting the quantity…",
        "Making that change for you…",
      ];
    }
    if (/add|move/.test(t)) {
      return [
        "Adding it to your bag…",
        "Placing it in your bag…",
        "Tucking it into your bag…",
      ];
    }
    return [
      "Opening your bag…",
      "Laying out your pieces…",
      "Gathering what you've chosen…",
    ];
  }

  if (/billing|checkout/.test(t)) {
    return [
      "Preparing your checkout…",
      "Getting your details in order…",
      "Readying everything for payment…",
    ];
  }
  if (/purchase|place_order|payment/.test(t)) {
    return [
      "Placing your order…",
      "Confirming your purchase…",
      "Securing your order…",
    ];
  }
  if (/order/.test(t)) {
    return [
      "Looking up your orders…",
      "Retrieving your order history…",
      "Finding your past purchases…",
    ];
  }

  if (/detail/.test(t)) {
    return [
      "Gathering the finer details…",
      "Reading the label for you…",
       "Taking a closer look…",
    ];
  }
  if (/new_arrival/.test(t)) {
    return [
      "Seeing what has just arrived…",
      "Unpacking the latest pieces…",
      "Browsing the new season…",
    ];
  }
  if (/sale/.test(t)) {
    return [
      "Looking through the sale…",
      "Finding the best offers…",
      "Seeking out the finest prices…",
    ];
  }
  if (/recommend|similar|complement/.test(t)) {
    return [
      "Styling a few ideas for you…",
      "Pairing pieces together…",
      "Picking out what suits you…",
    ];
  }
  if (/search|product|filter|categor|catalog/.test(t)) {
    return [
      "Browsing the collection…",
      "Looking through the racks…",
      "Hand-picking the best matches…",
      "Finding pieces you'll love…",
    ];
  }

  if (/session_restore|cache_sync/.test(t)) {
    return ["Picking up where we left off…", "Catching up on your visit…"];
  }

  return GENERIC_TOOL_LINES;
}

/**
 * Every way of saying one step. The lines of a step are interchangeable
 * phrasings of the same fact — none of them claims progress ("almost there")
 * the socket has not reported — so the loader may open on any of them and
 * move between them without ever leaving the backend's actual step.
 *
 * @param {{ phase?: string, tool?: string | null } | null | undefined} activity
 * @returns {string[]} never empty
 */
function agentActivityLines(activity) {
  switch (activity?.phase) {
    case AgentPhase.TRANSCRIBING:
      return ["Listening closely…", "Taking in every word…"];
    case AgentPhase.TOOL:
      return toolLines(activity.tool);
    case AgentPhase.WRAPPING:
      return ["Adding the finishing touches…", "One last touch…"];
    default:
      return ["One moment…", "Giving that some thought…", "Let me see…"];
  }
}

/**
 * The line the loader shows for an activity. `null` — a turn that has not
 * reached the socket yet — reads as thinking.
 *
 * `variant` picks among the step's phrasings and wraps, so a caller can hand
 * in any running count: a different one per turn (the same request does not
 * read identically twice) plus a slow tick while a step lingers.
 *
 * @param {{ phase?: string, tool?: string | null } | null | undefined} activity
 * @param {number} [variant=0]
 */
export function agentActivityLabel(activity, variant = 0) {
  const lines = agentActivityLines(activity);
  const n = Number.isFinite(variant) ? Math.abs(Math.trunc(variant)) : 0;
  return lines[n % lines.length];
}
